<template>
  <section class="money dark" id="offer">
    <div class="container">
      <div class="money-grid">
        <div class="money-lead" data-aos="fade-right">
          <section-title
            align="left"
            tone="dark"
            :kicker="kicker"
            :title="title"
            :lede="lede"
          />

          <div class="money-clock">
            <p class="money-clock-title">{{ clockLabel }}</p>

            <!-- One cell per unit, in the order the counter reads them. The
                 cells and the `data-type` hooks are identical on both promo
                 pages; only the hook that starts the countdown differs, and
                 that is the `live` prop rather than a second copy of the
                 markup. A static offer renders no hook, so no script runs. -->
            <div class="timer" :data-event="live ? 'timer' : null">
              <div v-for="unit in units" :key="unit.type" :data-type="unit.type">
                <span class="timer-digits">00</span>
                <span class="timer-label">{{ t(unit.label) }}</span>
              </div>
            </div>
          </div>
        </div>

        <figure class="money-poster" data-aos="fade-left">
          <img :src="poster" :alt="posterAlt" />
        </figure>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* ---- The offer band: ink, no cards --------------------------------------
   The opening band of both promotion landings: the section title, the offer
   window and the poster, on the promo ink ground. Extracted from the two
   pages, which held byte-identical copies of every rule below. */
.money {
  background: $ink;
  overflow: hidden;
  /* The comparison band below carries the other half of the gap. */
  padding-bottom: 4rem;
  position: relative;

  /* A single blue glow off the top-left, the same light the services bento
     carries, so the band is not a flat fill. */
  &::before {
    background: radial-gradient(50rem 28rem at 6% 0%, rgba($primary, .42), transparent 64%);
    content: '';
    inset: 0;
    pointer-events: none;
    position: absolute;
  }

  > .container {
    position: relative;
  }
}

.money-grid {
  align-items: center;
  display: grid;
  gap: 2.5rem;

  /* Stacked, the poster leads. The band is the offer, and on a phone the
     artwork is what says what is being offered before a word of the copy is
     read; the title, the lede and the countdown follow it. Only the visual
     order changes, so the document keeps its own order (offer copy, then the
     image that illustrates it) and a screen reader is not made to open on a
     figure. */
  @media (max-width: 991.98px) {
    .money-poster {
      order: -1;
    }
  }

  @media (min-width: 992px) {
    gap: 3.5rem;
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 0.7fr);
  }
}

/* Heading and offer window, beside the poster. */
.money-lead {
  min-width: 0;
}

/* The poster, mounted on the ink ground rather than floating on a card. */
.money-poster {
  border: 1px solid $ink-line;
  border-radius: $r-lg;
  box-shadow: $shadow-dark;
  margin: 0;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
  }
}

.money-clock {
  /* Label on its own line, the digits below it, so the countdown reads as a
     block rather than a sentence that happens to end in numbers. */
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  /* The section title above supplies the gap. */
  margin: 0;
}

/* The offer window's label is the shared $label-* step. It is the same label role
   as the section kicker directly above it, and on this ink ground it takes the
   same colour, so the two read as one column of labels. */
.money-clock-title {
  color: $label-on-dark;
  font-family: $headings-font-family;
  font-size: $label-size;
  font-weight: $label-weight;
  letter-spacing: $label-tracking;
  margin: 0;
  text-transform: uppercase;
}

/* Countdown on the ink ground, so it reads as part of the band rather than
   as a pale panel. The weekly counter drives the digits; a page whose offer
   has closed renders the same cells with no hook, so they stay on 00. */
.timer {
  display: flex;
  gap: 0.5rem;

  > div {
    align-items: center;
    background: $ink-fill;
    border: 1px solid $ink-line;
    border-radius: $r-md;
    display: flex;
    flex-direction: column;
    min-width: 4rem;
    padding: 0.6rem 0.45rem;
  }
}

.timer-digits {
  color: $on-dark-strong;
  font-family: $headings-font-family;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1;
}

/* A unit legend under the numerals, not a label naming a title, so it is the one
   thing on the site that does not take the shared $label-* step. It sits inside a
   4rem cell beside three others, and the step at 0.78rem measures wider than that
   cell: the label would overflow its own tile and the row would run past the
   container on a 375px phone. It keeps its own 0.6rem and the muted step. */
.timer-label {
  color: $on-dark-muted;
  font-family: $headings-font-family;
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  margin-top: 0.3rem;
  text-transform: uppercase;
}

/* The four cells are 4rem wide at every other size, which measures wider than
   the container on a 320px phone. The row trims to fit rather than wrapping or
   running past the band. */
@media (max-width: 575.98px) {
  .timer {
    gap: 0.35rem;

    > div {
      min-width: 3.35rem;
      padding: 0.5rem 0.3rem;
    }
  }

  .timer-digits {
    font-size: 1.15rem;
  }
}
</style>

<script>
import { startCountdown, stopCountdown } from '@/countdown';
import { useI18n } from 'vue-i18n';
import SectionTitle from '@/Components/SectionTitle.vue';

export default {
  components: {
    SectionTitle
  },

  props: {
    kicker: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      required: true
    },
    lede: {
      type: String,
      default: ''
    },
    /* The label above the counter. The two offers word it differently because
       one window has closed and the other is running. */
    clockLabel: {
      type: String,
      required: true
    },
    poster: {
      type: String,
      required: true
    },
    posterAlt: {
      type: String,
      default: ''
    },
    /* Whether the countdown ticks. Annual Pack runs the live counter; RM299's
       window has closed and stays on 00, which is the only difference between
       the two bands and was previously a whole duplicated script plus markup. */
    live: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      countdownHandle: null,
      stopped: false
    };
  },

  mounted() {
    if (!this.live) {
      return;
    }

    /* Count down to the coming Monday at midnight, so the window restarts
       weekly the same way the homepage counter does. */
    var end = new Date();
    var offset = (1 + 7 - end.getDay()) % 7;

    end.setDate(offset ? end.getDate() + offset : end.getDate() + 7);
    end.setHours(0, 0, 0, 0);

    startCountdown(this, end);
  },

  beforeUnmount() {
    this.stopped = true;
    stopCountdown(this);
  },

  setup() {
    /* The band's own words are removed from the dictionaries: the day, hour,
       minute and second labels and the band copy are shared chrome now, so the
       component reads them from the root composer like every other page. */
    const { t } = useI18n();

    const units = [
      { type: 'days', label: 'Days' },
      { type: 'hours', label: 'Hours' },
      { type: 'minutes', label: 'Minutes' },
      { type: 'seconds', label: 'Seconds' }
    ];

    return { t, units };
  }
};
</script>
